import type { APIKey, AppData, AuditEvent, AuthOptions, CatalogItem, Command, DomainBinding, InstallProfile, Meta, Node, Overview, Provider, ProviderType, ServiceRoute, TianNASIntegration } from './types'

const VITE_ENV = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env || {}
const API_BASE = (VITE_ENV.VITE_API_BASE || '').replace(/\/$/, '')
// The device-bound login component deliberately requires a same-origin fixed
// path. Vite proxies /api in development and the production console is served
// by the control plane, so auth must not use a cross-origin VITE_API_BASE.
const AUTH_BASE = ''
// Never guess a credential in production. Local development can still opt in
// explicitly with VITE_DEFAULT_ADMIN_TOKEN.
const DEFAULT_TOKEN = VITE_ENV.VITE_DEFAULT_ADMIN_TOKEN || ''
const ADMIN_TOKEN_KEY = 'resourcehub-admin-token'
const AUTH_METHOD_KEY = 'resourcehub-admin-auth-method'
const DEVICE_ID_KEY = 'resourcehub-device-id'
const PHONE_REFRESH_TOKEN_KEY = 'resourcehub-phone-refresh-token'
const PHONE_ACCESS_EXPIRES_KEY = 'resourcehub-phone-access-expires-at'
const PHONE_REMEMBER_UNTIL_KEY = 'resourcehub-phone-remember-until'
const PHONE_REMEMBER_WINDOW_MS = 24 * 60 * 60 * 1000
const DEVICE_KEY_REBIND_CODE = 'device_key_rebind_requires_step_up'
export const DEVICE_IDENTITY_RENEWED_CODE = 'auth_device_identity_renewed'

export class ApiError extends Error {
  status: number
  code: string

  constructor(message: string, status = 0, code = 'request_error') {
    super(message)
    this.status = status
    this.code = code
  }
}

export function getAdminToken(): string {
  return localStorage.getItem(ADMIN_TOKEN_KEY) || DEFAULT_TOKEN
}

export type AdminAuthMethod = 'phone' | 'token'

export function getAdminAuthMethod(): AdminAuthMethod {
  return localStorage.getItem(AUTH_METHOD_KEY) === 'phone' ? 'phone' : 'token'
}

export function setAdminToken(token: string, method: AdminAuthMethod = 'token'): void {
  localStorage.setItem(ADMIN_TOKEN_KEY, token.trim())
  localStorage.setItem(AUTH_METHOD_KEY, method)
  if (method !== 'phone') clearPhoneRefreshSession()
}

export function clearAdminToken(): void {
  localStorage.removeItem(ADMIN_TOKEN_KEY)
  localStorage.removeItem(AUTH_METHOD_KEY)
  clearPhoneRefreshSession()
}

function clearPhoneRefreshSession(): void {
  localStorage.removeItem(PHONE_REFRESH_TOKEN_KEY)
  localStorage.removeItem(PHONE_ACCESS_EXPIRES_KEY)
  localStorage.removeItem(PHONE_REMEMBER_UNTIL_KEY)
}

function persistPhoneSession(session: Record<string, unknown>, rememberUntil?: number): string {
  const accessToken = String(session.accessToken || session.token || '').trim()
  const refreshToken = String(session.refreshToken || '').trim()
  if (!accessToken || !refreshToken) throw new ApiError('登录成功响应缺少可续期会话')
  const now = Date.now()
  const expiresIn = Number(session.expiresIn)
  const accessExpiresAt = now + (Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn * 1000 : 15 * 60 * 1000)
  const upstreamRefreshExpiry = Number(session.refreshExpiresAt)
  const requestedRememberUntil = rememberUntil || now + PHONE_REMEMBER_WINDOW_MS
  const cappedRememberUntil = Number.isFinite(upstreamRefreshExpiry) && upstreamRefreshExpiry > now
    ? Math.min(requestedRememberUntil, upstreamRefreshExpiry)
    : requestedRememberUntil
  localStorage.setItem(ADMIN_TOKEN_KEY, accessToken)
  localStorage.setItem(AUTH_METHOD_KEY, 'phone')
  localStorage.setItem(PHONE_REFRESH_TOKEN_KEY, refreshToken)
  localStorage.setItem(PHONE_ACCESS_EXPIRES_KEY, String(accessExpiresAt))
  localStorage.setItem(PHONE_REMEMBER_UNTIL_KEY, String(cappedRememberUntil))
  return accessToken
}

let refreshPhoneSessionPromise: Promise<boolean> | null = null

async function refreshPhoneSession(): Promise<boolean> {
  if (refreshPhoneSessionPromise) return refreshPhoneSessionPromise
  refreshPhoneSessionPromise = (async () => {
    if (getAdminAuthMethod() !== 'phone') return false
    const refreshToken = localStorage.getItem(PHONE_REFRESH_TOKEN_KEY) || ''
    const rememberUntil = Number(localStorage.getItem(PHONE_REMEMBER_UNTIL_KEY) || 0)
    if (!refreshToken || !Number.isFinite(rememberUntil) || Date.now() >= rememberUntil) {
      clearAdminToken()
      return false
    }
    try {
      const response = await fetch(`${AUTH_BASE}/api/auth/refresh-token`, {
        method: 'POST', cache: 'no-store', credentials: 'omit', redirect: 'error',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok || payload?.success === false) {
        clearAdminToken()
        return false
      }
      const session = payload?.data && typeof payload.data === 'object' ? payload.data : payload
      persistPhoneSession(session as Record<string, unknown>, rememberUntil)
      return true
    } catch {
      return false
    }
  })().finally(() => { refreshPhoneSessionPromise = null })
  return refreshPhoneSessionPromise
}

async function ensureFreshPhoneSession(): Promise<void> {
  if (getAdminAuthMethod() !== 'phone') return
  const accessExpiresAt = Number(localStorage.getItem(PHONE_ACCESS_EXPIRES_KEY) || 0)
  if (accessExpiresAt > 0 && accessExpiresAt <= Date.now() + 30_000) await refreshPhoneSession()
}

export async function getAuthOptions(): Promise<AuthOptions> {
  let response: Response
  try {
    response = await fetch(`${AUTH_BASE}/api/v1/auth/options`, { cache: 'no-store', credentials: 'omit' })
  } catch (error) {
    throw new ApiError(error instanceof Error ? error.message : '无法读取登录配置')
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}))
    throw new ApiError(payload?.error?.message || payload?.message || '无法读取登录配置', response.status, payload?.error?.code || payload?.code || 'auth_options_error')
  }
  return response.json() as Promise<AuthOptions>
}

function deviceInfo() {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY) || ''
  if (!deviceId) {
    deviceId = `resourcehub-web-${crypto.randomUUID()}`
    localStorage.setItem(DEVICE_ID_KEY, deviceId)
  }
  return {
    deviceId,
    deviceName: 'TianCloud 管理控制台',
    deviceModel: navigator.platform || 'Web Browser',
    deviceType: 'admin',
    operatingSystem: navigator.platform || 'unknown',
    appVersion: '0.4.0',
    browser: navigator.userAgent,
  }
}

async function anonymousJSON<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${AUTH_BASE}${path}`, {
    method: 'POST', cache: 'no-store', credentials: 'omit', redirect: 'error',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || payload?.success === false) throw new ApiError(payload?.message || payload?.error?.message || '认证请求失败', response.status, payload?.code || payload?.error?.code || 'auth_error')
  return payload as T
}

let deviceLoginScript: Promise<void> | null = null
function loadDeviceLoginScript(): Promise<void> {
  const scope = window as Window & { TtNasDeviceBoundLogin?: { authenticate: (options: Record<string, unknown>) => Promise<Record<string, unknown>> } }
  if (scope.TtNasDeviceBoundLogin) return Promise.resolve()
  if (!deviceLoginScript) {
    deviceLoginScript = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = `${AUTH_BASE}/api/auth/device-bound-login-v1.js`
      script.async = true
      script.onload = () => resolve()
      script.onerror = () => reject(new ApiError('设备安全登录组件加载失败'))
      document.head.appendChild(script)
    })
  }
  return deviceLoginScript
}

export async function sendAdminPhoneCode(phoneNumber: string): Promise<void> {
  const device = deviceInfo()
  await anonymousJSON('/api/auth/send-code', { phoneNumber: phoneNumber.trim(), isAdmin: true, deviceId: device.deviceId })
}

export async function loginWithAdminPhone(phoneNumber: string, code: string): Promise<string> {
  await loadDeviceLoginScript()
  const scope = window as Window & { TtNasDeviceBoundLogin?: { authenticate: (options: Record<string, unknown>) => Promise<Record<string, unknown>> } }
  if (!scope.TtNasDeviceBoundLogin) throw new ApiError('设备安全登录组件不可用')
  let completed: Record<string, unknown>
  try {
    completed = await scope.TtNasDeviceBoundLogin.authenticate({
      expectedDevice: deviceInfo(), expectedLoginKind: 'phone_otp',
      primaryBody: { phoneNumber: phoneNumber.trim(), code: code.trim(), isAdminLogin: true },
    })
  } catch (error) {
    const errorCode = typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code?: unknown }).code || '')
      : ''
    if (errorCode !== DEVICE_KEY_REBIND_CODE) throw error

    // IndexedDB may be cleared independently from localStorage. In that case
    // the old device ID points at a server-bound key that this browser can no
    // longer prove it owns. Never overwrite that key from the login flow:
    // enroll a fresh device identity and require a new OTP instead.
    localStorage.setItem(DEVICE_ID_KEY, `resourcehub-web-${crypto.randomUUID()}`)
    throw new ApiError(
      '此浏览器的设备密钥已变化。旧设备公钥已安全保留，已创建新的本机设备身份；请重新获取短信验证码后登录。',
      409,
      DEVICE_IDENTITY_RENEWED_CODE,
    )
  }
  return persistPhoneSession(completed)
}

export async function logoutAdmin(): Promise<void> {
  const token = getAdminToken()
  const response = await fetch(`${AUTH_BASE}/api/auth/logout`, {
    method: 'POST', cache: 'no-store', credentials: 'omit', redirect: 'error',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: '{}',
  })
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}))
    throw new ApiError(payload?.message || '会话安全退出失败', response.status, payload?.code || 'logout_failed')
  }
  clearAdminToken()
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  await ensureFreshPhoneSession()
  const headers = new Headers(init.headers || {})
  let requestToken = getAdminToken()
  headers.set('Authorization', `Bearer ${requestToken}`)
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, { ...init, headers })
    if (response.status === 401 && getAdminAuthMethod() === 'phone') {
      if (getAdminToken() !== requestToken || await refreshPhoneSession()) {
        requestToken = getAdminToken()
        headers.set('Authorization', `Bearer ${requestToken}`)
        response = await fetch(`${API_BASE}${path}`, { ...init, headers })
      }
    }
  } catch (error) {
    throw new ApiError(error instanceof Error ? error.message : '无法连接 TianCloud API')
  }
  if (!response.ok) {
    let message = response.statusText
    let code = 'request_error'
    try {
      const payload = await response.json()
      message = payload?.error?.message || payload?.message || message
      code = payload?.error?.code || code
    } catch {
      // Keep status text.
    }
    throw new ApiError(message, response.status, code)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export async function checkAuth(): Promise<void> {
  await request('/api/v1/auth/check', { method: 'POST' })
}

export async function loadAppData(): Promise<AppData> {
  // Validate the session before fanning out to the protected data endpoints.
  // This keeps the unauthenticated login path quiet and produces one clear 401.
  await checkAuth()
  const [overview, nodes, providers, routes, commands, domains, catalog, apiKeys, providerTypes, audits, meta, install, tianNAS] = await Promise.all([
    request<Overview>('/api/v1/overview'),
    request<Node[]>('/api/v1/nodes'),
    request<Provider[]>('/api/v1/providers'),
    request<ServiceRoute[]>('/api/v1/routes'),
    request<Command[]>('/api/v1/commands?limit=120'),
    request<DomainBinding[]>('/api/v1/domains'),
    request<CatalogItem[]>('/api/v1/catalog'),
    request<APIKey[]>('/api/v1/api-keys'),
    request<ProviderType[]>('/api/v1/provider-types'),
    request<AuditEvent[]>('/api/v1/audit?limit=80'),
    request<Meta>('/api/v1/meta'),
    request<{ command: string }>('/api/v1/install-command'),
    request<TianNASIntegration>('/api/v1/tiantian-nas'),
  ])
  return { overview, nodes, providers, routes, commands, domains, catalog, apiKeys, providerTypes, audits, meta, installCommand: install.command, tianNAS }
}

export const api = {
  updateNode: (nodeId: string, payload: Partial<Pick<Node, 'name' | 'lifecycle' | 'trust_level' | 'management_mode' | 'location' | 'tags'>>) => request<Node>(`/api/v1/nodes/${nodeId}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteNode: (nodeId: string) => request<void>(`/api/v1/nodes/${nodeId}`, { method: 'DELETE' }),
  approveNode: (nodeId: string) => request<Node>(`/api/v1/nodes/${nodeId}/approve`, { method: 'POST' }),
  refreshNode: (nodeId: string) => request<Command>(`/api/v1/nodes/${nodeId}/refresh`, { method: 'POST' }),
  refreshAllNodes: () => request<{ commands: Command[]; queued: number; skipped: number }>('/api/v1/nodes/refresh', { method: 'POST' }),
	upgradeAgent: (nodeId: string) => request<Command>(`/api/v1/nodes/${nodeId}/agent-upgrade`, { method: 'POST' }),
  createInstallCommand: (profile: InstallProfile) => request<{ command: string }>('/api/v1/install-command', { method: 'POST', body: JSON.stringify(profile) }),
  createCommand: (nodeId: string, command: string) => request<Command>(`/api/v1/nodes/${nodeId}/commands`, { method: 'POST', body: JSON.stringify({ command }) }),
  installCapability: (nodeId: string, capability: string) => request<Command>(`/api/v1/nodes/${nodeId}/capabilities/${capability}/install`, { method: 'POST' }),
  upgradeCapability: (nodeId: string, capability: string) => request<Command>(`/api/v1/nodes/${nodeId}/capabilities/${capability}/upgrade`, { method: 'POST' }),
  createDomain: (nodeId: string, payload: Record<string, unknown>) => request<DomainBinding>(`/api/v1/nodes/${nodeId}/domains`, { method: 'POST', body: JSON.stringify(payload) }),
  createProvider: (payload: Record<string, unknown>) => request<Provider>('/api/v1/providers', { method: 'POST', body: JSON.stringify(payload) }),
  updateProvider: (id: string, payload: Record<string, unknown>) => request<Provider>(`/api/v1/providers/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deleteProvider: (id: string) => request<void>(`/api/v1/providers/${id}`, { method: 'DELETE' }),
  refreshProvider: (id: string) => request<Provider>(`/api/v1/providers/${id}/refresh`, { method: 'POST' }),
  setProviderUsage: (id: string, used: number, limit: number, unit: string) => request<Provider>(`/api/v1/providers/${id}/usage`, { method: 'POST', body: JSON.stringify({ mode: 'set', used, limit, unit }) }),
  updateRoute: (service: string, route: ServiceRoute) => request<ServiceRoute>(`/api/v1/routes/${service}`, { method: 'PUT', body: JSON.stringify(route) }),
  createAPIKey: (name: string) => request<{ api_key: APIKey; token: string }>('/api/v1/api-keys', { method: 'POST', body: JSON.stringify({ name }) }),
  revokeAPIKey: (id: string) => request<void>(`/api/v1/api-keys/${id}`, { method: 'DELETE' }),
}
